// MARK://dhk8/admin-shell/pages/OnboardingPage
// Onboarding wizard (steps 2–7 of the spec; step 1 = /signup). A new SANDBOX tenant walks
// through:
//   2. Organización  — razón social, RFC, régimen fiscal, giro (vertical fija = Restaurante)
//   3. Matriz/sucursal — nombre de la sucursal principal, dirección, zona horaria
//   4. Ingredientes  — ~4 ingredientes de ejemplo con costo por unidad (1 tortilla, 1 kg de carne…)
//   5. Receta        — cuánto usa de cada ingrediente por porción → costo calculado → precio de venta → producto
//   6. Grupos/variantes — "cuántos tacos por porción: 4" + mismo precio o descuento (% / monto)
//   7. Cierre        — "Listo, vamos a tu POS" → redirect a pos.nintaigo.com/?tour=1 (modo guiado;
//                      el tour + el banner "ENTORNO DE PRUEBAS" los construye el equipo del POS)
// Los PINs de POS se envían al owner por correo después del provisioning; el
// merchant los regenera desde admin > usuarios, no desde el wizard.
// Requiere sesión (la deja /signup con el token del owner).
import { useEffect, useState } from 'react'
import { Navigate } from 'react-router-dom'
import { Check, Trash2, Plus, Rocket, Copy } from 'lucide-react'
import { useAuthStore } from '../stores/auth-store.js'
import { getFiscalSettings, saveFiscalSettings, REGIMES } from '../lib/clients/fiscal.js'
import { createSection, saveTable, getLayout } from '../lib/clients/floor.js'
import { DEFAULT_INGREDIENTS, DEFAULT_RECIPE, portionCostCents, createIngredient, createRecipeProduct, createPortionGroup } from '../lib/clients/recipes.js'
import { listCategories } from '../lib/clients/catalog.js'
import type { FiscalSettings, Ingredient, RecipeLine } from '../lib/mock-data.js'
import { useAsync } from '../lib/use-async.js'
import { mxn } from '../lib/money.js'
import BrandMark from '../components/BrandMark.js'
import MoneyInput from '../components/MoneyInput.js'
import UnitPicker, { unitShort } from '../components/UnitPicker.js'
import ThemeSwitcher from '../components/ThemeSwitcher.js'

// pos.nintaigo.com in prod; locally point at the POS preview / dev port.
const POS_URL = (import.meta.env.VITE_POS_URL as string | undefined)
  ?? (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname) ? 'http://localhost:5174' : 'https://pos.nintaigo.com')

// PINs (paso 6) se eliminó: las credenciales de POS se envían al correo del owner
// después del provisioning. Mantener la fila visible aquí distraía y duplicaba
// trabajo (regenerar PINs ya vive en admin > usuarios).
const STEP_LABELS = ['Organización', 'Sucursal', 'Ingredientes', 'Receta', 'Grupos', 'Listo']

interface WizardData {
  ingredients: Ingredient[]
  recipe: RecipeLine[]
  productName: string
  salePriceCents: number
  createdProductId?: string
  // Step 2 (Branch) — se persiste en backend, pero mantenemos el form draft.
  branchName: string
  branchAddress: string
  branchTimezone: string
}

// ── Persistencia local (sandbox demo) ──────────────────────────────────────
// El onboarding es educativo para el cliente: si recarga la página NO debe
// perder lo que llevaba. Persistimos `step` + `data` en localStorage por
// tenantId (clave única para evitar mezclar wizards entre cuentas distintas).
const LS_KEY_PREFIX = 'dhk8:admin:onboarding'
function lsKey(tenantId: string | null): string {
  return `${LS_KEY_PREFIX}:${tenantId ?? 'anonymous'}`
}
interface PersistedWizard { step: number; data: WizardData }
function loadPersisted(tenantId: string | null): PersistedWizard | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(lsKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedWizard
    if (typeof parsed.step !== 'number') return null
    if (!parsed.data || !Array.isArray(parsed.data.ingredients)) return null
    return parsed
  } catch { return null }
}
function savePersisted(tenantId: string | null, w: PersistedWizard): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(lsKey(tenantId), JSON.stringify(w)) } catch { /* quota / private mode */ }
}
function clearPersisted(tenantId: string | null): void {
  if (typeof window === 'undefined') return
  try { window.localStorage.removeItem(lsKey(tenantId)) } catch { /* noop */ }
}

const errText = (e: unknown) => e instanceof Error ? e.message : String(e)

/** Inline error banner for a wizard step — never hard-blocks: offers Reintentar + Saltar. */
function StepError({ message, onRetry, onSkip }: { message: string; onRetry: () => void; onSkip: () => void }) {
  return (
    <div className="adm-banner adm-banner--danger" role="alert" style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span>No se pudo guardar: {message}</span>
      <button className="adm-btn adm-btn--sm" onClick={onRetry}>Reintentar</button>
      <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={onSkip}>Saltar por ahora</button>
    </div>
  )
}

export default function OnboardingPage() {
  const session = useAuthStore((s) => s.session)
  const tenantId = useAuthStore((s) => s.session?.tenantId ?? '')
  // Hydrate desde localStorage por tenant; si no hay nada guardado, defaults.
  const initial = (typeof window !== 'undefined' ? loadPersisted(tenantId) : null) ?? {
    step: 0,
    data: {
      ingredients: structuredClone(DEFAULT_INGREDIENTS),
      recipe: structuredClone(DEFAULT_RECIPE),
      productName: 'Tacos al pastor (orden)',
      salePriceCents: 0,
      branchName: 'Matriz',
      branchAddress: '',
      branchTimezone: 'America/Mexico_City',
    },
  }
  const [step, setStep] = useState<number>(initial.step)
  const [data, setData] = useState<WizardData>(initial.data)
  // Persist cada cambio. tenantId puede ser null antes de hidratar session.
  useEffect(() => {
    if (!session) return
    savePersisted(tenantId, { step, data })
  }, [step, data, tenantId, session])
  if (!session) return <Navigate to="/login" replace />

  return (
    <div className="adm-auth">
      <div style={{ position: 'absolute', top: 12, right: 12 }}><ThemeSwitcher /></div>
      <div className="adm-card" style={{ width: 'min(680px, 100%)' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 8 }}><BrandMark size="md" tagline="Onboarding" /></div>
        <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
          {STEP_LABELS.map((label, i) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: i === step ? 'var(--pos-fg)' : 'var(--pos-fg-tertiary)', fontWeight: i === step ? 600 : 400 }}>
              <span style={{ width: 20, height: 20, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: i < step ? 'var(--pos-success-bg)' : i === step ? 'var(--pos-tint-fill)' : 'var(--pos-elevated)', color: i < step ? 'var(--pos-success)' : 'var(--pos-tint-on)' }}>{i < step ? <Check size={12} /> : i + 1}</span>
              {label}
            </div>
          ))}
        </div>
        {step === 0 && <StepOrg onNext={() => setStep(1)} />}
        {step === 1 && <StepBranch data={data} setData={setData} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {step === 2 && <StepIngredients data={data} setData={setData} onBack={() => setStep(1)} onNext={() => setStep(3)} />}
        {step === 3 && <StepRecipe data={data} setData={setData} onBack={() => setStep(2)} onNext={() => setStep(4)} />}
        {step === 4 && <StepGroups data={data} onBack={() => setStep(3)} onNext={() => setStep(5)} />}
        {step === 5 && <StepDone />}
      </div>
      <style>{`.adm-auth { min-height: 100dvh; display: flex; align-items: flex-start; justify-content: center; padding: var(--pos-space-6) var(--pos-space-4) var(--pos-space-10); background: var(--pos-canvas); position: relative; overflow-y: auto; }`}</style>
    </div>
  )
}

// ── 2. Organización ────────────────────────────────────────────────────────
function StepOrg({ onNext }: { onNext: () => void }) {
  const { data, setData } = useAsync<FiscalSettings>((s) => getFiscalSettings(s))
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  function patch(p: Partial<FiscalSettings>) { if (data) setData({ ...data, ...p }) }
  async function next() {
    if (!data) return
    setSaving(true); setErr(null)
    try { await saveFiscalSettings(data); onNext() }
    catch (e) { setErr(errText(e)) }
    finally { setSaving(false) }
  }
  return (
    <div>
      <h2 className="adm-card__title">Organización</h2>
      {!data ? <div className="adm-empty">Cargando…</div> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <label className="adm-field"><span className="adm-field__label">Razón social</span><input className="adm-input" value={data.legalName} onChange={(e) => patch({ legalName: e.target.value })} /></label>
          <label className="adm-field"><span className="adm-field__label">RFC</span><input className="adm-input" value={data.rfc} onChange={(e) => patch({ rfc: e.target.value.toUpperCase() })} /></label>
          <label className="adm-field"><span className="adm-field__label">Régimen fiscal</span><select className="adm-select" value={data.regimeCode} onChange={(e) => { const r = REGIMES.find((x) => x.code === e.target.value); patch({ regimeCode: e.target.value, regimeName: r?.name ?? '' }) }}>{REGIMES.map((r) => <option key={r.code} value={r.code}>{r.code} — {r.name}</option>)}</select></label>
          <label className="adm-field"><span className="adm-field__label">Giro</span><select className="adm-select" value="restaurant" disabled><option value="restaurant">Restaurante</option></select><span className="adm-row__meta" style={{ marginTop: 4 }}>Por ahora solo Restaurante; pronto habrá más giros.</span></label>
          {err && <StepError message={err} onRetry={next} onSkip={onNext} />}
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}><button className="adm-btn adm-btn--primary" onClick={next} disabled={saving || !data.rfc.trim()}>Continuar</button></div>
        </div>
      )}
    </div>
  )
}

// ── 3. Matriz / sucursal ───────────────────────────────────────────────────
const TIMEZONES = ['America/Mexico_City', 'America/Tijuana', 'America/Cancun', 'America/Monterrey']
function StepBranch({ data, setData, onBack, onNext }: { data: WizardData; setData: (d: WizardData) => void; onBack: () => void; onNext: () => void }) {
  // Lee/escribe sobre el wizard data (persiste a localStorage).
  const name = data.branchName
  const address = data.branchAddress
  const tz = data.branchTimezone
  const setName = (v: string) => setData({ ...data, branchName: v })
  const setAddress = (v: string) => setData({ ...data, branchAddress: v })
  const setTz = (v: string) => setData({ ...data, branchTimezone: v })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  async function next() {
    setBusy(true); setErr(null)
    try {
      // The provisioning saga already created a default branch + base floor; here we just
      // rename the "main" section and nudge the first table so the layout is real.
      const layout = await getLayout().catch(() => ({ sections: [], tables: [] }))
      if (layout.sections[0]) await createSection(name)
      if (layout.tables[0]) await saveTable({ ...layout.tables[0] })
      onNext()
    } catch (e) { setErr(errText(e)) }
    finally { setBusy(false) }
  }
  return (
    <div>
      <h2 className="adm-card__title">Matriz / sucursal principal</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="adm-field"><span className="adm-field__label">Nombre de la sucursal</span><input className="adm-input" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label className="adm-field"><span className="adm-field__label">Dirección</span><input className="adm-input" value={address} onChange={(e) => setAddress(e.target.value)} placeholder="Calle, número, colonia, ciudad" /></label>
        <label className="adm-field"><span className="adm-field__label">Zona horaria</span><select className="adm-select" value={tz} onChange={(e) => setTz(e.target.value)}>{TIMEZONES.map((z) => <option key={z} value={z}>{z}</option>)}</select></label>
        {err && <StepError message={err} onRetry={next} onSkip={onNext} />}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><button className="adm-btn" onClick={onBack}>Atrás</button><button className="adm-btn adm-btn--primary" onClick={next} disabled={busy || !name.trim()}>Continuar</button></div>
      </div>
    </div>
  )
}

// ── 4. Ingredientes ────────────────────────────────────────────────────────
function StepIngredients({ data, setData, onBack, onNext }: { data: WizardData; setData: (d: WizardData) => void; onBack: () => void; onNext: () => void }) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  function patchIng(i: number, p: Partial<Ingredient>) { setData({ ...data, ingredients: data.ingredients.map((x, j) => j === i ? { ...x, ...p } : x) }) }
  function addIng() { setData({ ...data, ingredients: [...data.ingredients, { id: `ing-new-${data.ingredients.length}`, name: '', unit: 'pieza', costCents: 0 }] }) }
  function removeIng(i: number) { setData({ ...data, ingredients: data.ingredients.filter((_, j) => j !== i), recipe: data.recipe.filter((r) => r.ingredientId !== data.ingredients[i]!.id) }) }
  async function next() {
    setBusy(true); setErr(null)
    try {
      // Create each ingredient as a real INGREDIENT product; map its old (mock) id → new id so
      // the recipe lines in the next step reference real product ids.
      const idMap = new Map<string, string>()
      const created: Ingredient[] = []
      for (const ing of data.ingredients) {
        if (!ing.name.trim()) continue
        const real = await createIngredient({ name: ing.name, unit: ing.unit, costCents: ing.costCents })
        idMap.set(ing.id, real.id)
        created.push(real)
      }
      const remappedRecipe = data.recipe.map((r) => ({ ...r, ingredientId: idMap.get(r.ingredientId) ?? r.ingredientId })).filter((r) => created.some((c) => c.id === r.ingredientId))
      setData({ ...data, ingredients: created, recipe: remappedRecipe })
      onNext()
    } catch (e) { setErr(errText(e)) }
    finally { setBusy(false) }
  }
  return (
    <div>
      <h2 className="adm-card__title">Ingredientes</h2>
      <p className="adm-row__meta" style={{ marginBottom: 12 }}>Todavía no hay nada cargado. Vamos a crear unos ingredientes de ejemplo con su costo — el costo de <em>una</em> unidad (una tortilla, un kilo de carne, etc.). Podrás cambiarlos o agregar más en Inventario.</p>
      <div className="adm-card adm-card--pad0"><div className="adm-list">
        {data.ingredients.map((ing, i) => (
          <div key={i} className="adm-row" style={{ cursor: 'default', gap: 8, flexWrap: 'wrap' }}>
            <input className="adm-input adm-btn--sm" style={{ flex: '1 1 140px' }} value={ing.name} onChange={(e) => patchIng(i, { name: e.target.value })} placeholder="Ingrediente" />
            <div style={{ flex: '0 1 170px' }}><UnitPicker bare value={ing.unit} onChange={(unit) => patchIng(i, { unit })} /></div>
            <span className="adm-row__meta">costo de 1 {unitShort(ing.unit)}:</span>
            <span style={{ display: 'inline-block', width: 100 }}><MoneyInput valueCents={ing.costCents} onChangeCents={(c) => patchIng(i, { costCents: c })} /></span>
            <button className="adm-btn adm-btn--ghost adm-btn--sm adm-btn--danger" onClick={() => removeIng(i)}><Trash2 size={14} /></button>
          </div>
        ))}
      </div></div>
      <button className="adm-btn adm-btn--sm" style={{ marginTop: 10 }} onClick={addIng}><Plus size={14} /> Agregar ingrediente</button>
      {err && <StepError message={err} onRetry={next} onSkip={onNext} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}><button className="adm-btn" onClick={onBack}>Atrás</button><button className="adm-btn adm-btn--primary" onClick={next} disabled={busy || data.ingredients.every((i) => !i.name.trim())}>Continuar</button></div>
    </div>
  )
}

// ── 5. Receta → producto ───────────────────────────────────────────────────
/**
 * Input numérico decimal-friendly: mantiene el texto en buffer local mientras
 * escribes y solo notifica al padre cuando parsea a número válido. Resuelve:
 *  - tipear "0.5" carácter a carácter (sin que React re-renderice el "0.")
 *  - decimal con coma O punto (`0,5` y `0.5` ambos válidos)
 *  - campo vacío en lugar de "0" cuando value === 0 (placeholder visible).
 */
function NumericInput({
  value,
  onChange,
  placeholder,
  className,
  style,
}: {
  value: number
  onChange: (n: number) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
}) {
  const [text, setText] = useState<string>(value > 0 ? String(value) : '')
  useEffect(() => {
    // Re-sync solo si el value del padre cambió y NO matchea el buffer parseado.
    const parsed = Number(text.replace(',', '.'))
    if (value !== parsed && !(Number.isNaN(parsed) && value === 0)) {
      setText(value > 0 ? String(value) : '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])
  return (
    <input
      className={className}
      style={style}
      inputMode="decimal"
      placeholder={placeholder ?? '0'}
      value={text}
      onChange={(e) => {
        const raw = e.target.value
        setText(raw)
        // Permite coma como separador decimal (es-MX). Si está vacío o es solo
        // un signo/punto intermedio, no notificamos: aún en edición.
        const normalized = raw.replace(',', '.')
        if (normalized === '' || normalized === '.' || normalized.endsWith('.')) return
        const n = Number(normalized)
        if (Number.isFinite(n) && n >= 0) onChange(n)
      }}
      onBlur={() => {
        const normalized = text.replace(',', '.')
        const n = Number(normalized)
        if (!Number.isFinite(n) || n < 0) {
          setText(value > 0 ? String(value) : '')
        } else {
          onChange(n)
          setText(n > 0 ? String(n) : '')
        }
      }}
    />
  )
}

function StepRecipe({ data, setData, onBack, onNext }: { data: WizardData; setData: (d: WizardData) => void; onBack: () => void; onNext: () => void }) {
  const cats = useAsync((s) => listCategories(s))
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const liveIngredients = data.ingredients.filter((i) => i.name.trim())
  const recipe = data.recipe.filter((r) => liveIngredients.some((i) => i.id === r.ingredientId))
  const cost = portionCostCents(recipe, data.ingredients)
  function patchLine(ingredientId: string, qty: number) {
    const exists = data.recipe.some((r) => r.ingredientId === ingredientId)
    setData({ ...data, recipe: exists ? data.recipe.map((r) => r.ingredientId === ingredientId ? { ...r, qtyPerPortion: qty } : r) : [...data.recipe, { ingredientId, qtyPerPortion: qty }] })
  }
  async function next() {
    setBusy(true); setErr(null)
    try {
      const product = await createRecipeProduct({ productName: data.productName, sku: data.productName.slice(0, 8).toUpperCase().replace(/\s/g, '') || `PROD${Date.now().toString(36).slice(-4).toUpperCase()}`, categoryId: cats.data?.[0]?.id ?? '', recipe, ingredients: data.ingredients, salePriceCents: data.salePriceCents })
      setData({ ...data, createdProductId: product.id })
      onNext()
    } catch (e) { setErr(errText(e)) }
    finally { setBusy(false) }
  }
  return (
    <div>
      <h2 className="adm-card__title">Receta de una porción</h2>
      <p className="adm-row__meta" style={{ marginBottom: 12 }}>¿Cuánto usas de cada ingrediente por porción? (p. ej. 2 tortillas, 50 g = 0.05 kg de carne, etc.)</p>
      <label className="adm-field" style={{ marginBottom: 12 }}><span className="adm-field__label">Nombre del producto</span><input className="adm-input" value={data.productName} onChange={(e) => setData({ ...data, productName: e.target.value })} /></label>
      <div className="adm-card adm-card--pad0"><div className="adm-list">
        {liveIngredients.map((ing) => {
          const line = data.recipe.find((r) => r.ingredientId === ing.id)
          return (
            <div key={ing.id} className="adm-row" style={{ cursor: 'default', gap: 8 }}>
              <div className="adm-row__main"><div className="adm-row__title">{ing.name}</div><div className="adm-row__meta">{mxn(ing.costCents)} / {unitShort(ing.unit)}</div></div>
              <NumericInput
                className="adm-input adm-btn--sm"
                style={{ width: 90, textAlign: 'right' }}
                value={line?.qtyPerPortion ?? 0}
                onChange={(n) => patchLine(ing.id, n)}
                placeholder="0"
              />
              <span className="adm-row__meta" style={{ minWidth: 36, fontWeight: 600 }}>{unitShort(ing.unit)}</span>
            </div>
          )
        })}
      </div></div>
      <div className="adm-banner" style={{ marginTop: 12, background: 'var(--pos-info-bg)', color: 'var(--pos-info)' }}>Costo calculado de la porción: <strong>{mxn(cost)}</strong></div>
      <label className="adm-field" style={{ marginTop: 12 }}>
        <span className="adm-field__label">Precio de venta (MXN)</span>
        <NumericInput
          className="adm-input"
          value={data.salePriceCents / 100}
          onChange={(n) => setData({ ...data, salePriceCents: Math.round(n * 100) })}
          placeholder={`sugerido ≥ ${mxn(Math.ceil(cost * 3 / 100) * 100)}`}
        />
      </label>
      {err && <StepError message={err} onRetry={next} onSkip={onNext} />}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}><button className="adm-btn" onClick={onBack}>Atrás</button><button className="adm-btn adm-btn--primary" onClick={next} disabled={busy || data.salePriceCents <= 0 || !data.productName.trim()}>Continuar</button></div>
    </div>
  )
}

// ── 6. Grupos / variantes ──────────────────────────────────────────────────
function StepGroups({ data, onBack, onNext }: { data: WizardData; onBack: () => void; onNext: () => void }) {
  const [groupName, setGroupName] = useState('Cuántos tacos por porción')
  const [optionName, setOptionName] = useState('4 tacos')
  const [multiplier, setMultiplier] = useState(4)
  const [discKind, setDiscKind] = useState<'none' | 'percent' | 'amount'>('none')
  const [discValue, setDiscValue] = useState(0)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const gross = data.salePriceCents * multiplier
  const final = discKind === 'percent' ? Math.round(gross * (1 - discValue / 100)) : discKind === 'amount' ? Math.max(0, gross - discValue * 100) : gross
  async function next() {
    setBusy(true); setErr(null)
    try {
      await createPortionGroup({ productId: data.createdProductId ?? '', groupName, optionName, multiplier, basePortionPriceCents: data.salePriceCents, discount: { kind: discKind, value: discKind === 'amount' ? discValue * 100 : discValue } })
      onNext()
    } catch (e) { setErr(errText(e)) }
    finally { setBusy(false) }
  }
  return (
    <div>
      <h2 className="adm-card__title">Grupos y variantes</h2>
      <p className="adm-row__meta" style={{ marginBottom: 12 }}>Crea un grupo de opciones para tu producto — por ejemplo "cuántos tacos por porción". Puedes mantener el precio proporcional o aplicar un descuento. Después podrás crear más grupos, opciones y accesos desde Catálogo.</p>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <label className="adm-field"><span className="adm-field__label">Nombre del grupo</span><input className="adm-input" value={groupName} onChange={(e) => setGroupName(e.target.value)} /></label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label className="adm-field" style={{ flex: 1, minWidth: 140 }}><span className="adm-field__label">Opción</span><input className="adm-input" value={optionName} onChange={(e) => setOptionName(e.target.value)} /></label>
          <label className="adm-field" style={{ width: 110 }}><span className="adm-field__label">× porciones base</span><input className="adm-input" type="number" min={1} value={multiplier} onChange={(e) => setMultiplier(Math.max(1, Number(e.target.value)))} /></label>
        </div>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <label className="adm-field" style={{ width: 170 }}><span className="adm-field__label">Descuento</span><select className="adm-select" value={discKind} onChange={(e) => setDiscKind(e.target.value as 'none' | 'percent' | 'amount')}><option value="none">Sin descuento (proporcional)</option><option value="percent">Porcentaje</option><option value="amount">Monto fijo</option></select></label>
          {discKind !== 'none' && <label className="adm-field" style={{ width: 110 }}><span className="adm-field__label">{discKind === 'percent' ? '%' : 'MXN'}</span><input className="adm-input" inputMode="decimal" value={String(discValue)} onChange={(e) => setDiscValue(Number(e.target.value) || 0)} /></label>}
        </div>
        <div className="adm-banner" style={{ background: 'var(--pos-info-bg)', color: 'var(--pos-info)' }}>Precio de "{optionName}": <strong>{mxn(final)}</strong> {discKind !== 'none' && <span style={{ textDecoration: 'line-through', opacity: .6, marginLeft: 6 }}>{mxn(gross)}</span>}</div>
        {err && <StepError message={err} onRetry={next} onSkip={onNext} />}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}><button className="adm-btn" onClick={onBack}>Atrás</button><button className="adm-btn adm-btn--primary" onClick={next} disabled={busy || !groupName.trim() || !optionName.trim()}>Continuar</button></div>
      </div>
    </div>
  )
}

// ── 6. Cierre + ir al POS ──────────────────────────────────────────────────
function readPosCreds(): { posPinId: string | null; posLoginPin: string | null } | null {
  if (typeof sessionStorage === 'undefined') return null
  try { const raw = sessionStorage.getItem('dhk8-pos-creds'); return raw ? JSON.parse(raw) : null } catch { return null }
}
function StepDone() {
  const creds = readPosCreds()
  const tenantId = useAuthStore((s) => s.session?.tenantId ?? '')
  function copy(text: string) { void navigator.clipboard?.writeText(text) }
  // Onboarding terminó: limpiamos el wizard draft de localStorage. Si la
  // persona vuelve a /onboarding luego, arranca fresh en paso 0.
  useEffect(() => { clearPersisted(tenantId) }, [tenantId])
  return (
    <div style={{ textAlign: 'center' }}>
      <h2 className="adm-card__title" style={{ marginBottom: 8 }}>¡Todo listo!</h2>
      <p className="adm-row__meta" style={{ marginBottom: 16 }}>Tu negocio quedó configurado en <strong>modo de pruebas</strong>. Te llevamos a tu POS con un tour guiado; cuando estés lista para operar de verdad, pasa a Modo en vivo desde el admin (Inicio → "Pasar a Modo en vivo"). En pruebas puedes ajustar todo sin preocuparte.</p>
      {(creds?.posPinId || creds?.posLoginPin) && (
        <div className="adm-banner" role="status" style={{ marginBottom: 16, background: 'var(--pos-info-bg)', color: 'var(--pos-info)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span>Para entrar al POS usa — <strong>ID de POS:</strong> <code>{creds?.posPinId ?? '—'}</code> · <strong>PIN:</strong> <code>{creds?.posLoginPin ?? '—'}</code> — te los enviamos también por correo.</span>
          <button className="adm-btn adm-btn--ghost adm-btn--sm" onClick={() => copy(`ID de POS: ${creds?.posPinId ?? '—'} · PIN: ${creds?.posLoginPin ?? '—'}`)}><Copy size={14} /> Copiar</button>
        </div>
      )}
      <a className="adm-btn adm-btn--primary" href={`${POS_URL}/?tour=1`} style={{ display: 'inline-flex' }}><Rocket size={16} /> Listo, vamos a tu POS</a>
    </div>
  )
}
